import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute(
  "/admin/_layout/inventory/deployment-manifests"
)({
  component: DeploymentManifestsPage,
});

import { useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { SidebarInset } from "@/components/ui/sidebar";
import { Input } from "@/components/ui/input";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { ConfirmDialog } from "@/components/crud/ConfirmDialog/ConfirmDialog";
import {
  useFetchDeploymentManifests,
  useDeploymentManifestMutations,
} from "@/hooks/api/inventory/useDeploymentManifests";
import {
  useFetchItems,
  useFetchWarehouses,
  ALL_ACTIVE_PARAMS,
} from "@/hooks/api/inventory/useCatalog";
import { useFetchInspectionChecklists } from "@/hooks/api/inventory/useCollectionsLabels";
import {
  useFetchAssetItems,
  useAssetItemMutations,
  ALL_ASSET_ITEMS_PAGE_SIZE,
} from "@/hooks/api/inventory/useAssetItems";
import { DeploymentManifestTable } from "@/components/inventory/tables/DeploymentManifestTable";
import { DeploymentManifestFormSheet } from "@/components/inventory/forms/DeploymentManifestFormSheet";
import { ManifestPartialReturnModal } from "@/components/inventory/forms/ManifestPartialReturnModal";
import { InspectReturnModal } from "@/components/inventory/forms/InspectReturnModal";
import { DeploymentManifestDetailSheet } from "@/components/inventory/forms/DetailViewSheet";
import { fetchTasks } from "@/hooks/api/tasks/useFetchTask";
import {
  fetchOrgUsers,
  orgUserDisplayName,
} from "@/hooks/api/user/fetchOrgUsers";
import { RemoteSearchSelect } from "@/components/reusable-components/remote-search-select";
import { useOrganizationStore } from "@/stores/useOrganizationStore";
import type {
  DeploymentManifest,
  DeploymentManifestPayload,
  InspectReturnPayload,
  ManifestPartialReturnPayload,
} from "@/types/inventory";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";
import { RETURN_INSPECTION_ELIGIBLE_STATUSES } from "@/lib/inventory";

const DEPLOYMENT_MANIFEST_STATUS_OPTIONS = [
  { label: "Draft", value: "DRAFT" },
  { label: "Issued", value: "ISSUED" },
  { label: "Acknowledged", value: "ACKNOWLEDGED" },
  { label: "Partially Returned", value: "PARTIALLY_RETURNED" },
  { label: "Closed", value: "CLOSED" },
  { label: "Cancelled", value: "CANCELLED" },
];

function DeploymentManifestsPage() {
  const navigate = useNavigate();
  // DeploymentManifestViewSet's required_permission_map leaves list/retrieve/
  // acknowledge baseline (None) -- any custodian may see and acknowledge
  // their own manifests -- so the page itself is never wrapped in
  // ProtectedRoute; only the write actions below need this.
  const { data: capabilities } = useMyPermissions();
  const canManage = can(capabilities, "inventory.rental.manage");
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  const [showFilters, setShowFilters] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined
  );
  const [warehouseFilter, setWarehouseFilter] = useState<string | undefined>(
    undefined
  );
  const [custodianFilter, setCustodianFilter] = useState<string | undefined>(
    undefined
  );
  const [taskFilter, setTaskFilter] = useState<string | undefined>(undefined);

  const resetFilters = () => {
    setSearchFilter("");
    setStatusFilter(undefined);
    setWarehouseFilter(undefined);
    setCustodianFilter(undefined);
    setTaskFilter(undefined);
    setPage(1);
  };

  const manifestsQuery = useFetchDeploymentManifests({
    page_size: pageSize,
    page,
    search: searchFilter || undefined,
    status: statusFilter,
    source_warehouse: warehouseFilter ? parseInt(warehouseFilter) : undefined,
    custodian_user: custodianFilter ? parseInt(custodianFilter) : undefined,
    task: taskFilter ? parseInt(taskFilter) : undefined,
  });
  const manifests = manifestsQuery.data?.results ?? [];
  const count = manifestsQuery.data?.count ?? 0;

  const itemsQuery = useFetchItems(ALL_ACTIVE_PARAMS);
  const items = itemsQuery.data?.results ?? [];
  const warehousesQuery = useFetchWarehouses(ALL_ACTIVE_PARAMS);
  const warehouses = warehousesQuery.data?.results ?? [];
  const { orgSlug } = useOrganizationStore();
  const checklistsQuery = useFetchInspectionChecklists(ALL_ACTIVE_PARAMS);
  const checklists = checklistsQuery.data?.results ?? [];
  const {
    createManifest,
    updateManifest,
    deleteManifest,
    issueManifest,
    acknowledgeManifest,
    partialReturnManifest,
    cancelManifest,
  } = useDeploymentManifestMutations();
  const { bulkInspectReturn } = useAssetItemMutations();

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editing, setEditing] = useState<DeploymentManifest | null>(null);
  const [viewing, setViewing] = useState<DeploymentManifest | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeploymentManifest | null>(
    null
  );
  const [returnTarget, setReturnTarget] = useState<DeploymentManifest | null>(
    null
  );
  const [inspectTarget, setInspectTarget] = useState<DeploymentManifest | null>(
    null
  );
  const [issueTarget, setIssueTarget] = useState<DeploymentManifest | null>(
    null
  );
  const [acknowledgeTarget, setAcknowledgeTarget] =
    useState<DeploymentManifest | null>(null);
  const [cancelTarget, setCancelTarget] = useState<DeploymentManifest | null>(
    null
  );

  const inspectAssetItemsQuery = useFetchAssetItems(
    {
      deployment_manifest: inspectTarget?.id,
      status_in: [...RETURN_INSPECTION_ELIGIBLE_STATUSES],
      page_size: ALL_ASSET_ITEMS_PAGE_SIZE,
    },
    { enabled: !!inspectTarget }
  );
  const inspectAssetItems = inspectAssetItemsQuery.data?.results ?? [];

  const isMutating =
    createManifest.isPending ||
    updateManifest.isPending ||
    deleteManifest.isPending ||
    issueManifest.isPending ||
    acknowledgeManifest.isPending ||
    partialReturnManifest.isPending ||
    cancelManifest.isPending;
  const isLoading = manifestsQuery.isPending || isMutating;
  const isFetching = manifestsQuery.isFetching || isMutating;

  const handleSubmit = async (data: DeploymentManifestPayload) => {
    if (editing) {
      await updateManifest.mutateAsync({ id: editing.id, payload: data });
      toast.success("Manifest updated");
    } else {
      await createManifest.mutateAsync(data);
      toast.success("Manifest created");
    }
    setIsSheetOpen(false);
    setEditing(null);
  };

  const handleIssue = (manifest: DeploymentManifest) => {
    setIssueTarget(manifest);
  };

  const handleAcknowledge = (manifest: DeploymentManifest) => {
    setAcknowledgeTarget(manifest);
  };

  const handlePartialReturn = async (payload: ManifestPartialReturnPayload) => {
    if (!returnTarget) return;
    await partialReturnManifest.mutateAsync({ id: returnTarget.id, payload });
    toast.success("Return processed");
    setReturnTarget(null);
  };

  const handleReturnInspect = async (
    payload: InspectReturnPayload,
    assetItemIds?: number[]
  ) => {
    if (!assetItemIds?.length) return;
    await bulkInspectReturn.mutateAsync({ assetItemIds, payload });
    toast.success("Return Inspection recorded");
    setInspectTarget(null);
  };

  const handleCancel = (manifest: DeploymentManifest) => {
    setCancelTarget(manifest);
  };

  const handleIssueConfirm = async () => {
    if (!issueTarget) return;
    try {
      await issueManifest.mutateAsync({ id: issueTarget.id });
      toast.success("Manifest issued");
      setIssueTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleAcknowledgeConfirm = async () => {
    if (!acknowledgeTarget) return;
    try {
      await acknowledgeManifest.mutateAsync({ id: acknowledgeTarget.id });
      toast.success("Manifest acknowledged");
      setAcknowledgeTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    try {
      await cancelManifest.mutateAsync({ id: cancelTarget.id });
      toast.success("Manifest cancelled");
      setCancelTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteManifest.mutateAsync({ id: deleteTarget.id });
      toast.success("Manifest deleted");
      setDeleteTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Deployment Manifests"
          description="Track assets issued to custodians and manage their returns."
          isLoading={isFetching}
          onToggleFilter={() => setShowFilters((v) => !v)}
          onAdd={
            canManage
              ? () => {
                  setEditing(null);
                  setIsSheetOpen(true);
                }
              : undefined
          }
          addLabel="Manifest"
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by manifest number..."
              value={searchFilter}
              onChange={(e) => {
                setSearchFilter(e.target.value);
                setPage(1);
              }}
              className="h-9 pl-8"
            />
          </div>
          <SelectFilter
            placeholder="Status"
            options={DEPLOYMENT_MANIFEST_STATUS_OPTIONS}
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              setPage(1);
            }}
          />
          <SelectFilter
            placeholder="Warehouse"
            options={warehouses.map((w) => ({
              label: w.name,
              value: String(w.id),
            }))}
            value={warehouseFilter}
            onChange={(v) => {
              setWarehouseFilter(v);
              setPage(1);
            }}
          />
          <RemoteSearchSelect
            className="h-9"
            queryKey={["org-users", orgSlug]}
            fetchPage={(q) => fetchOrgUsers(orgSlug, q)}
            getId={(u) => u.id}
            getLabel={orgUserDisplayName}
            value={custodianFilter ? parseInt(custodianFilter) : null}
            onChange={(id) => {
              setCustodianFilter(id != null ? String(id) : undefined);
              setPage(1);
            }}
            placeholder="Custodian"
            searchPlaceholder="Search users..."
          />
          <RemoteSearchSelect
            className="h-9"
            queryKey={["tasks", "admin"]}
            fetchPage={(q) => fetchTasks(q, true)}
            getId={(t) => t.id}
            getLabel={(t) => t.title}
            value={taskFilter ? parseInt(taskFilter) : null}
            onChange={(id) => {
              setTaskFilter(id != null ? String(id) : undefined);
              setPage(1);
            }}
            placeholder="Task"
            searchPlaceholder="Search tasks..."
          />
        </FiltersPanel>

        <DeploymentManifestTable
          manifests={manifests}
          isLoading={isLoading}
          onNameClick={(m) =>
            navigate({
              to: "/admin/inventory/deployment-manifests/$id",
              params: { id: String(m.id) },
            })
          }
          onView={(m) => setViewing(m)}
          onEdit={(m) => {
            setEditing(m);
            setIsSheetOpen(true);
          }}
          onIssue={handleIssue}
          onAcknowledge={handleAcknowledge}
          onPartialReturn={(m) => setReturnTarget(m)}
          onReturnInspect={(m) => setInspectTarget(m)}
          onCancel={handleCancel}
          onDelete={(m) => setDeleteTarget(m)}
          canManage={canManage}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={setPage}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </div>

      <DeploymentManifestDetailSheet
        isOpen={!!viewing}
        onClose={() => setViewing(null)}
        manifest={viewing}
        onReturnInspect={(m) => setInspectTarget(m)}
      />

      <InspectReturnModal
        isOpen={!!inspectTarget}
        onClose={() => setInspectTarget(null)}
        onSubmit={handleReturnInspect}
        assetItem={null}
        contextLabel={
          <>
            Manifest:{" "}
            <span className="font-mono font-medium text-foreground">
              {inspectTarget?.manifest_number}
            </span>
          </>
        }
        assetItems={inspectAssetItems}
        requireAssetSelection
        checklists={checklists}
        isLoading={bulkInspectReturn.isPending}
      />

      <DeploymentManifestFormSheet
        isOpen={isSheetOpen}
        onClose={() => {
          setIsSheetOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSubmit}
        editing={editing}
        items={items}
        warehouses={warehouses}
        isLoading={createManifest.isPending || updateManifest.isPending}
      />

      <ManifestPartialReturnModal
        isOpen={!!returnTarget}
        onClose={() => setReturnTarget(null)}
        onSubmit={handlePartialReturn}
        manifest={returnTarget}
        isLoading={partialReturnManifest.isPending}
      />

      <DeleteConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Manifest"
        description={`Delete manifest "${deleteTarget?.manifest_number}"? This cannot be undone.`}
        itemName={deleteTarget?.manifest_number}
        isLoading={deleteManifest.isPending}
        destructive
      />

      <DeleteConfirmDialog
        isOpen={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancelConfirm}
        title="Cancel Manifest"
        description="Cancel this deployment manifest? This cannot be undone."
        itemName={cancelTarget?.manifest_number}
        isLoading={cancelManifest.isPending}
        confirmLabel="Cancel Manifest"
        loadingLabel="Cancelling..."
        destructive
      />

      <ConfirmDialog
        isOpen={!!issueTarget}
        onClose={() => setIssueTarget(null)}
        onConfirm={handleIssueConfirm}
        isLoading={issueManifest.isPending}
        title="Issue Manifest"
        description={`Issue manifest "${issueTarget?.manifest_number}"? Stock will be deducted from the source warehouse.`}
        confirmLabel="Issue"
        loadingLabel="Issuing..."
      />

      <ConfirmDialog
        isOpen={!!acknowledgeTarget}
        onClose={() => setAcknowledgeTarget(null)}
        onConfirm={handleAcknowledgeConfirm}
        isLoading={acknowledgeManifest.isPending}
        title="Acknowledge Manifest"
        description={`Acknowledge receipt of manifest "${acknowledgeTarget?.manifest_number}"?`}
        confirmLabel="Acknowledge"
        loadingLabel="Acknowledging..."
      />
    </SidebarInset>
  );
}
